import { CartAddEvent } from '@theme/events';

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.selected = [];
    this.size = Number(this.dataset.bundleSize || 4);
    this.abortController = new AbortController();
  }

  connectedCallback() {
    const { signal } = this.abortController;
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('change', this.onVariantChange, { signal });
    this.cleanProductTitles();
    this.render();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  cleanProductTitles() {
    this.querySelectorAll('.bundle-product__title').forEach((title) => {
      title.textContent = title.textContent.replace(/(?:\s*-\s*|\s+)(?:UK|EU|ROW)\s*$/i, '').trim();
    });
  }

  onClick = (event) => {
    const toggle = event.target.closest('[data-select-product]');
    if (toggle) return this.toggleProduct(toggle.closest('[data-product-card]'));

    if (event.target.closest('[data-collapse]')) return this.toggleTray();
    if (event.target.closest('[data-add-bundle]')) {
      if (this.selected.length < this.size) return this.scrollToProducts();
      return this.addBundle();
    }

    const remove = event.target.closest('[data-remove-slot]');
    if (remove) this.removeProduct(remove.dataset.removeSlot);

  };

  onVariantChange = (event) => {
    if (!event.target.matches('[data-variant]')) return;
    const card = event.target.closest('[data-product-card]');
    const option = event.target.selectedOptions?.[0];
    const price = card?.querySelector('[data-price]');
    if (price && option?.dataset.priceFormatted) price.textContent = option.dataset.priceFormatted;

    const selectedItem = this.selected.find((item) => item.card === card);
    if (selectedItem) {
      selectedItem.variantId = event.target.value;
      selectedItem.image = option?.dataset.image || selectedItem.image;
      this.render();
    }
  };

  toggleProduct(card) {
    if (!card) return;
    const existing = this.selected.find((item) => item.card === card);
    if (existing) return this.removeProduct(existing.variantId, card);
    if (this.selected.length >= this.size) {
      this.showMessage(`Your bundle already has ${this.size} items. Remove one to choose another.`, true);
      return;
    }

    const variant = card.querySelector('[data-variant]');
    const option = variant?.selectedOptions?.[0];
    if (!variant?.value) return;
    this.selected.push({
      card,
      variantId: variant.value,
      title: card.querySelector('.bundle-product__title')?.textContent.trim() || '',
      image: option?.dataset.image || variant.dataset.image || card.querySelector('img')?.currentSrc || '',
    });
    this.render();
  }

  removeProduct(variantId, exactCard = null) {
    const index = this.selected.findIndex((item) => exactCard ? item.card === exactCard : item.variantId === variantId);
    if (index < 0) return;
    this.selected.splice(index, 1);
    this.render();
  }

  render() {
    this.querySelectorAll('[data-product-card]').forEach((card) => {
      const active = this.selected.some((item) => item.card === card);
      card.classList.toggle('is-selected', active);
      const button = card.querySelector('[data-select-product]');
      button?.setAttribute('aria-pressed', String(active));
      button?.querySelector('[data-select-label]')?.toggleAttribute('hidden', active);
      button?.querySelector('[data-remove-label]')?.toggleAttribute('hidden', !active);
    });

    const slots = this.querySelectorAll('[data-slot]');
    slots.forEach((slot, index) => {
      const item = this.selected[index];
      slot.classList.toggle('bundle-slot--empty', !item);
      if (!item) {
        slot.innerHTML = `<span class="bundle-slot__number">${index + 1}</span><span class="bundle-slot__empty-text">${this.dataset.emptyLabel.replace('[count]', '')}</span>`;
        return;
      }
      slot.innerHTML = `${item.image ? `<img class="bundle-slot__image" src="${this.escape(item.image)}" alt="">` : ''}<div class="bundle-slot__details"><span class="bundle-slot__title">${this.escape(item.title)}</span><span class="bundle-slot__quantity">x 1</span></div><button type="button" class="bundle-slot__remove" data-remove-slot="${this.escape(item.variantId)}" aria-label="Remove ${this.escape(item.title)}">×</button>`;
    });

    const remaining = Math.max(0, this.size - this.selected.length);
    const progress = this.querySelector('[data-progress]');
    if (progress) progress.textContent = `${this.selected.length} of ${this.size} selected`;
    const button = this.querySelector('[data-add-bundle]');
    if (button) {
      button.setAttribute('aria-disabled', String(remaining > 0));
      const label = button.querySelector('[data-action-label]');
      if (label) {
        label.textContent = remaining ? this.incompleteLabel(remaining) : this.dataset.addLabel;
      }
    }
  }

  incompleteLabel(count) {
    let label = this.dataset.emptyLabel.replace('[count]', String(count));
    if (count !== 1 && /item$/i.test(label)) label += 's';
    return label;
  }

  toggleTray() {
    const tray = this.querySelector('[data-tray]');
    const button = this.querySelector('[data-collapse]');
    const collapsed = tray?.classList.toggle('is-collapsed') || false;
    button?.setAttribute('aria-expanded', String(!collapsed));
    button?.setAttribute('aria-label', collapsed ? 'Expand bundle selection' : 'Collapse bundle selection');
  }

  scrollToProducts() {
    this.querySelector('.bundle-builder__grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async addBundle() {
    if (this.selected.length !== this.size || this.loading) return;
    this.loading = true;
    const button = this.querySelector('[data-add-bundle]');
    const label = button?.querySelector('[data-action-label]');
    if (button) button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = this.dataset.addingLabel;
    this.showMessage('', false);

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const bundleId = this.dataset.sectionId + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: this.selected.map((item) => ({ id: Number(item.variantId), quantity: 1, properties: { '_Bundle': bundleId, '_Bundle title': 'Build Your Own Bundle' } })),
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this bundle.');

      this.dispatchEvent(new CartAddEvent(result, this.id, {
        source: 'product-form-component',
        itemCount: this.selected.length,
        sections: result.sections,
      }));
      this.showMessage(this.dataset.addedLabel, false);
      this.selected = [];
      this.render();
    } catch (error) {
      this.showMessage(error.message || 'Unable to add this bundle. Please try again.', true);
      this.render();
    } finally {
      this.loading = false;
      if (button) button.setAttribute('aria-disabled', String(this.selected.length !== this.size));
    }
  }

  showMessage(message, isError) {
    const element = this.querySelector('[data-message]');
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
    element.classList.toggle('is-error', Boolean(isError));
  }

  escape(value) {
    const span = document.createElement('span');
    span.textContent = String(value || '');
    return span.innerHTML;
  }
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
